/**
 * AdminNav — grouped top-bar navigation for the admin panel.
 *
 * The flat 12-button row outgrew the header at wider zoom levels and on
 * laptops with smaller viewports. This collapses related items into
 * dropdowns:
 *   - Dashboard         (standalone)
 *   - Customers ▾       Users, Orders, Support Tickets
 *   - Services ▾        Hosting, VPS, Email, Domains, Mail Domains, Hosting Accounts
 *   - Infrastructure ▾  Host Density, S3 Backups
 *
 * A group highlights itself in brand orange when its current tab is one of
 * its items, so the user always knows which area they're in.
 *
 * Click to open a dropdown, click outside or pick an item to close. Escape
 * also closes. Hover-to-open is intentionally not implemented — it traps
 * users on touch devices and confuses the cursor on accidental hovers.
 */
import React, { useEffect, useRef, useState } from 'react';

type AnyTabId = string;

interface NavItem {
  id: AnyTabId;
  name: string;
  icon: string;
}

export interface NavGroupEntry {
  kind: 'group';
  label: string;
  icon: string;
  items: NavItem[];
}
export interface NavItemEntry {
  kind: 'item';
  item: NavItem;
}
export type NavEntry = NavGroupEntry | NavItemEntry;

interface Props {
  entries: NavEntry[];
  activeTab: AnyTabId;
  onNavigate: (id: AnyTabId) => void;
}

const AdminNav: React.FC<Props> = ({ entries, activeTab, onNavigate }) => {
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when the user clicks outside it. Without this an
  // accidentally-opened group would stay open until another nav item was
  // clicked — annoying when the user just wants to return to content.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target as Node)) setOpenLabel(null);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenLabel(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const isActiveItem = (id: AnyTabId) => id === activeTab;
  const isActiveGroup = (g: NavGroupEntry) => g.items.some(i => isActiveItem(i.id));

  return (
    <nav ref={navRef} className="flex space-x-2">
      {entries.map((entry, idx) => {
        if (entry.kind === 'item') {
          const active = isActiveItem(entry.item.id);
          return (
            <button
              key={entry.item.id}
              onClick={() => { onNavigate(entry.item.id); setOpenLabel(null); }}
              className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                active
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{entry.item.icon}</span>
              {entry.item.name}
            </button>
          );
        }
        // group
        const active = isActiveGroup(entry);
        const isOpen = openLabel === entry.label;
        return (
          <div key={`${entry.label}-${idx}`} className="relative">
            <button
              onClick={() => setOpenLabel(isOpen ? null : entry.label)}
              className={`py-4 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 flex items-center ${
                active
                  ? 'border-brand-orange text-brand-orange'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              aria-expanded={isOpen}
              aria-haspopup="true"
            >
              <span className="mr-2">{entry.icon}</span>
              {entry.label}
              <svg
                className={`ml-1.5 h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isOpen && (
              <div
                className="absolute left-0 top-full mt-0 w-56 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-50"
                role="menu"
              >
                {entry.items.map(item => {
                  const active = isActiveItem(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => { onNavigate(item.id); setOpenLabel(null); }}
                      role="menuitem"
                      className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                        active
                          ? 'bg-brand-orange/10 text-brand-orange font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-2 w-5">{item.icon}</span>
                      {item.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default AdminNav;
